import { useEffect, useMemo } from "react";

import { ModelStatusTypes } from "@prisma/client";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";

import BlogPosts from "@musicfy/components/Dashboard/BlogPosts";
import CreateVoice from "@musicfy/components/Dashboard/CreateVoice";
import ModelsCarousel from "@musicfy/components/Dashboard/ModelsCarousel";
import RecentlyCreated from "@musicfy/components/Dashboard/RecentlyCreated";
import Welcome from "@musicfy/components/Dashboard/Welcome";
import { ModelCategory } from "@musicfy/components/RVCInference/types";
import { useModels } from "@musicfy/libs/ModelsProvider/hooks";
import { type IModel } from "@musicfy/libs/ModelsProvider/IModels";
import { isModelTrending } from "@musicfy/libs/ModelsProvider/utils";
import { usePaddleContext } from "@musicfy/libs/PaddleProvider";
import { useSubscriptionContext } from "@musicfy/libs/SubscriptionProvider";
import { api } from "@musicfy/utils";
import { useIsMobile } from "@musicfy/utils/hooks";
import { getVoicePickerUrl } from "@musicfy/utils/voicePickerUrl";

// Carousel portals into the navigation layout and cannot render in Node.
const Carousel = dynamic(
  () => import("@musicfy/components/Dashboard/Carousel"),
  { ssr: false }
);

const Dashboard: INextPage = () => {
  const router = useRouter();

  const { models } = useModels();
  const { startCheckoutFlow } = usePaddleContext();
  const { setSubscription } = useSubscriptionContext();
  const isMobile = useIsMobile();
  const { mutateAsync: syncStripeCheckoutSession } =
    api.subscription.syncStripeCheckoutSession.useMutation();
  const { mutateAsync: syncStripeConsumerSubscription } =
    api.subscription.syncStripeConsumerSubscription.useMutation();

  const carouselLimit = isMobile ? 10 : 20;

  const trendingModels = useMemo(
    () =>
      models
        .filter(
          (model: IModel) =>
            !model.isInstrumental &&
            model.status === ModelStatusTypes.completed &&
            !!model.usageCount &&
            model.usageCount > 0 &&
            !model.isPrivate &&
            isModelTrending(model)
        )
        .sort((a, b) => (b?.trendingScore || 0) - (a?.trendingScore || 0))
        .slice(0, carouselLimit),
    [models, carouselLimit]
  );

  const copyrightFreeModels = useMemo(
    () =>
      models
        .filter(
          (model: IModel) =>
            !model.isInstrumental &&
            model.status === ModelStatusTypes.completed &&
            !!model.usageCount &&
            model.usageCount > 0 &&
            !model.isPrivate &&
            model.isCopyrightFree &&
            !model.isCommunityModel
        )
        .sort((a, b) => (b?.usageCount || 0) - (a?.usageCount || 0))
        .slice(0, carouselLimit),
    [models, carouselLimit]
  );

  const instrumentalModels = useMemo(
    () =>
      models
        .filter(
          (model: IModel) =>
            model.isInstrumental &&
            model.status === ModelStatusTypes.completed &&
            !!model.usageCount &&
            model.usageCount > 0 &&
            !model.isPrivate
        )
        .sort((a, b) => (b?.usageCount || 0) - (a?.usageCount || 0))
        .slice(0, carouselLimit),
    [models, carouselLimit]
  );

  const popularModels = useMemo(
    () =>
      models
        .filter(
          (model: IModel) =>
            !model.isInstrumental &&
            model.status === ModelStatusTypes.completed &&
            !!model.usageCount &&
            model.usageCount > 0 &&
            !model.isPrivate &&
            model.isCopyrightFree
        )
        .sort((a, b) => (b?.usageCount || 0) - (a?.usageCount || 0))
        .slice(0, carouselLimit),
    [models, carouselLimit]
  );

  const parodyModels = useMemo(
    () =>
      models
        .filter(
          (model: IModel) =>
            !model.isInstrumental &&
            model.status === ModelStatusTypes.completed &&
            !!model.usageCount &&
            model.usageCount > 0 &&
            !model.isPrivate &&
            !model.isCopyrightFree &&
            !model.isInstrumental
        )
        .sort((a, b) => (b?.usageCount || 0) - (a?.usageCount || 0))
        .slice(4, 4 + carouselLimit),
    [models, carouselLimit]
  );

  useEffect(() => {
    const sessionId = router.query.session_id;
    const subscriptionId = router.query.subscription_id;
    if (router.query.checkout_success !== "1") {
      return;
    }

    const sync =
      typeof subscriptionId === "string"
        ? syncStripeConsumerSubscription({ subscriptionId })
        : typeof sessionId === "string"
          ? syncStripeCheckoutSession({ sessionId })
          : null;

    if (!sync) {
      return;
    }

    void sync
      .then((syncedSubscription) => {
        setSubscription(syncedSubscription);
      })
      .catch((error) => {
        console.error("Unable to sync completed Stripe Checkout", error);
      })
      .finally(() => {
        const clear = { ...router.query };
        delete clear.checkout_success;
        delete clear.session_id;
        delete clear.subscription_id;
        void router.replace(
          { pathname: router.pathname, query: clear },
          undefined,
          { shallow: true }
        );
      });
  }, [
    router,
    router.query.checkout_success,
    router.query.session_id,
    router.query.subscription_id,
    setSubscription,
    syncStripeCheckoutSession,
    syncStripeConsumerSubscription,
  ]);

  useEffect(() => {
    // Support both `?show_pricing` and `?show_pricing=1`.
    if (router.query.show_pricing === undefined) {
      return;
    }

    startCheckoutFlow("public-pricing-link");
  }, [router.query.show_pricing, startCheckoutFlow]);

  return (
    <div>
      <Carousel />
      <div className="w-full h-[40vh] lg:h-[70vh] min-h-[280px] lg:min-h-[400px] lg:max-h-[600px] overflow-hidden" />
      <div className="w-full -mt-16 lg:-mt-28 z-30 relative">
        <div className="w-full grid xl:grid-cols-7 grid-cols-1 gap-3">
          <div className="flex flex-col gap-3 xl:col-span-4">
            <Welcome />
            <CreateVoice />
          </div>
          <div className="rounded-[18px] border border-white/15 bg-purple/5 backdrop-blur-xl xl:px-12 xl:py-8 p-6 xl:col-span-3 overflow-y-scroll">
            <RecentlyCreated />
          </div>
        </div>
      </div>
      <div className="mt-8 lg:mt-12">
        <ModelsCarousel
          models={copyrightFreeModels}
          title="Copyright-Free Voices"
          subtitle="Safe for commercial use — start here if you're new to Musicfy"
          viewAllHref={getVoicePickerUrl(ModelCategory.COPYRIGHT_FREE)}
        />
      </div>
      <div className="mt-8 lg:mt-12">
        <ModelsCarousel
          models={instrumentalModels}
          title="Instrumentals"
          subtitle="Royalty-free instrumentals ready for your next project"
          viewAllHref={getVoicePickerUrl(ModelCategory.INSTRUMENTALS)}
        />
      </div>
      <div className="mt-8 lg:mt-12">
        <ModelsCarousel
          models={trendingModels}
          title="Trending Voices"
          viewAllHref={getVoicePickerUrl(ModelCategory.TRENDING)}
        />
      </div>
      <div className="mt-8 lg:mt-12">
        <ModelsCarousel models={popularModels} title="Popular Voices" />
      </div>
      <div className="mt-12 hidden lg:block">
        <ModelsCarousel models={parodyModels} title="Parody Voices" />
      </div>
      <div className="mt-9 hidden lg:block">
        <BlogPosts />
      </div>
    </div>
  );
};

Dashboard.seo = true;

export default Dashboard;
